package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.protocol.AnswerCategory
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

private val Sky = listOf(Color(0xFFB3E5FC), Color(0xFFFFF3E0))
private val Mountains = Color(0xFFB0BEC5)
private val Road = Color(0xFF424242)
private val Cliff = Color(0xFF8D6E63)
private val Abyss = listOf(Color(0xFF4E342E), Color(0xFF1B0F0B))
private val CarBody = Color(0xFFD32F2F)
private val CarWindow = Color(0xFFB3E5FC)
private val Tire = Color(0xFF212121)
private val Canopy = listOf(Color(0xFFFFB300), Color(0xFF1E88E5))
private val BridgeWood = Color(0xFFA1887F)

private const val ABYSS_START = .54f
private const val ABYSS_END = .76f
private const val ROAD_TOP = .6f
private const val ROAD_BOTTOM = .72f

@Composable
internal fun SpeedcarScene(counts: Map<AnswerCategory, Int>, modifier: Modifier = Modifier) {
    val transition = rememberInfiniteTransition()
    val drive by transition.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(900, easing = LinearEasing)),
    )
    val bounce by transition.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(420, easing = FastOutSlowInEasing), RepeatMode.Reverse),
    )
    val wobble by transition.animateFloat(
        initialValue = -5f,
        targetValue = 5f,
        animationSpec = infiniteRepeatable(tween(1_400, easing = FastOutSlowInEasing), RepeatMode.Reverse),
    )
    Box(modifier.fillMaxWidth().aspectRatio(16f / 9f).clip(RoundedCornerShape(24.dp))) {
        Canvas(Modifier.fillMaxSize()) {
            drawRect(Brush.verticalGradient(Sky))
            drawMountains()
            drawRoadAndAbyss(drive)
            drawBridge()
            val car = Offset(size.width * .3f, size.height * ROAD_TOP - size.height * .012f * bounce)
            drawParachute(car, wobble)
            drawSpeedLines(car, drive)
            drawCar(car, wheelTurn = drive * 360f)
        }
        SceneCornerBadges(
            topStart = AnswerCategory.PARACHUTE,
            topEnd = AnswerCategory.BRIDGE,
            bottomStart = AnswerCategory.ENGINE,
            bottomEnd = AnswerCategory.ABYSS,
            counts = counts,
        )
    }
}

private fun DrawScope.drawMountains() {
    val path = Path().apply {
        moveTo(0f, size.height * ROAD_TOP)
        lineTo(size.width * .14f, size.height * .32f)
        lineTo(size.width * .3f, size.height * .46f)
        lineTo(size.width * .48f, size.height * .26f)
        lineTo(size.width * .66f, size.height * .44f)
        lineTo(size.width * .84f, size.height * .3f)
        lineTo(size.width, size.height * .42f)
        lineTo(size.width, size.height * ROAD_TOP)
        close()
    }
    drawPath(path, Mountains)
}

private fun DrawScope.drawRoadAndAbyss(drive: Float) {
    val roadTop = size.height * ROAD_TOP
    val roadBottom = size.height * ROAD_BOTTOM
    drawRect(Cliff, Offset(0f, roadBottom), Size(size.width, size.height - roadBottom))
    val gap = Path().apply {
        moveTo(size.width * ABYSS_START, roadTop)
        lineTo(size.width * (ABYSS_START + .02f), size.height * .9f)
        lineTo(size.width * (ABYSS_START + .05f), size.height)
        lineTo(size.width * (ABYSS_END - .04f), size.height)
        lineTo(size.width * (ABYSS_END - .015f), size.height * .92f)
        lineTo(size.width * ABYSS_END, roadTop)
        close()
    }
    drawPath(gap, Brush.verticalGradient(Abyss, startY = roadTop, endY = size.height))
    drawRect(Road, Offset(0f, roadTop), Size(size.width * ABYSS_START, roadBottom - roadTop))
    drawRect(Road, Offset(size.width * ABYSS_END, roadTop), Size(size.width * (1 - ABYSS_END), roadBottom - roadTop))
    val dash = size.width * .06f
    val laneY = (roadTop + roadBottom) / 2
    var x = -dash * 2 + (1 - drive) * dash * 2
    while (x < size.width * ABYSS_START) {
        drawLine(
            Color.White,
            Offset(x, laneY),
            Offset(minOf(x + dash, size.width * ABYSS_START), laneY),
            strokeWidth = size.height * .012f,
        )
        x += dash * 2
    }
}

private fun DrawScope.drawBridge() {
    val roadTop = size.height * ROAD_TOP
    val left = size.width * ABYSS_START
    val right = size.width * ABYSS_END
    val plank = size.height * .025f
    drawRect(BridgeWood, Offset(left, roadTop), Size(right - left, plank))
    val planks = 9
    repeat(planks) { index ->
        val x = left + (right - left) * index / planks
        drawLine(Color(0xFF6D4C41), Offset(x, roadTop), Offset(x, roadTop + plank), strokeWidth = size.height * .004f)
    }
    val arch = Path().apply {
        moveTo(left, roadTop + plank)
        quadraticTo((left + right) / 2, roadTop + size.height * .16f, right, roadTop + plank)
    }
    drawPath(arch, Color(0xFF6D4C41), style = Stroke(size.height * .01f))
    repeat(5) { index ->
        val t = (index + 1) / 6f
        val x = left + (right - left) * t
        val archY = roadTop + plank + size.height * .14f * 4 * t * (1 - t)
        drawLine(Color(0xFF6D4C41), Offset(x, roadTop + plank), Offset(x, archY), strokeWidth = size.height * .005f)
    }
}

private fun DrawScope.drawParachute(car: Offset, wobble: Float) {
    val anchor = Offset(car.x - size.width * .1f, car.y - size.height * .06f)
    val canopyCenter = Offset(size.width * .12f, size.height * .4f)
    val radius = size.height * .13f
    rotate(wobble - 12f, pivot = anchor) {
        listOf(-1f, 0f, 1f).forEach { side ->
            drawLine(
                Color(0xFF5D4037),
                Offset(canopyCenter.x + side * radius * .9f, canopyCenter.y),
                anchor,
                strokeWidth = size.height * .004f,
            )
        }
        val segments = 4
        repeat(segments) { segment ->
            drawArc(
                Canopy[segment % Canopy.size],
                startAngle = 180f + segment * 180f / segments,
                sweepAngle = 180f / segments,
                useCenter = true,
                topLeft = canopyCenter - Offset(radius, radius),
                size = Size(radius * 2, radius * 2),
            )
        }
    }
}

private fun DrawScope.drawSpeedLines(car: Offset, drive: Float) {
    repeat(3) { line ->
        val phase = (drive + line / 3f) % 1f
        val y = car.y - size.height * (.03f + line * .03f)
        val end = car.x - size.width * .1f - phase * size.width * .05f
        drawLine(
            Color.White.copy(alpha = 1f - phase),
            Offset(end - size.width * .08f, y),
            Offset(end, y),
            strokeWidth = size.height * .008f,
            cap = StrokeCap.Round,
        )
    }
}

private fun DrawScope.drawCar(bottomCenter: Offset, wheelTurn: Float) {
    val length = size.width * .25f
    val height = size.height * .095f
    val wheelRadius = size.height * .05f
    val body = Offset(bottomCenter.x - length / 2, bottomCenter.y - wheelRadius - height * .6f)
    val cabin = Path().apply {
        moveTo(body.x + length * .25f, body.y)
        lineTo(body.x + length * .36f, body.y - height * .75f)
        lineTo(body.x + length * .68f, body.y - height * .75f)
        lineTo(body.x + length * .82f, body.y)
        close()
    }
    drawPath(cabin, CarBody)
    val window = Path().apply {
        moveTo(body.x + length * .32f, body.y - height * .05f)
        lineTo(body.x + length * .39f, body.y - height * .6f)
        lineTo(body.x + length * .66f, body.y - height * .6f)
        lineTo(body.x + length * .75f, body.y - height * .05f)
        close()
    }
    drawPath(window, CarWindow)
    drawRoundRect(CarBody, body, Size(length, height), CornerRadius(height * .4f))
    drawRect(Color.White, Offset(body.x + length * .44f, body.y + height * .15f), Size(length * .14f, height * .5f))
    drawCircle(Color(0xFFFFEB3B), height * .14f, Offset(body.x + length - height * .2f, body.y + height * .3f))
    listOf(.22f, .78f).forEach { position ->
        val center = Offset(body.x + length * position, bottomCenter.y - wheelRadius)
        drawCircle(Tire, wheelRadius, center)
        drawCircle(Color(0xFF9E9E9E), wheelRadius * .5f, center)
        rotate(wheelTurn, pivot = center) {
            repeat(3) { spoke ->
                val angle = spoke * 120f * PI.toFloat() / 180f
                drawLine(
                    Tire,
                    center,
                    center + Offset(cos(angle), sin(angle)) * (wheelRadius * .5f),
                    strokeWidth = wheelRadius * .18f,
                )
            }
        }
    }
}
